

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { 
  Coins, 
  LayoutDashboard, 
  Menu, 
  X, 
  PlusCircle, 
  Moon, 
  Sun, 
  Activity,
  Trash2 
} from "lucide-react";

import { GetStakingRiskProductsThunk } from "../../Store/Thunks/StakingRiskThunk";

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const dispatch = useDispatch();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false); 
 
  const products = useSelector((state)=>{return state.StakingRiskReducer.stakingRiskList})
    const deleted = useSelector(
      (state) => state.StakingRiskReducer.deleted
    );
    useEffect(() => {   
          dispatch(GetStakingRiskProductsThunk())
  
    }, [deleted]);

    useEffect(() => {
      // Check local storage for dark mode preference
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode) {
        setDarkMode(JSON.parse(savedMode));
      }
    }, []);

    const toggleDarkMode = () => {
      const newMode = !darkMode;
      setDarkMode(newMode);
      localStorage.setItem('darkMode', JSON.stringify(newMode));
    };

    const isActiveProductPage = (productId) => {
      return currentPageName === "ProductDetail" && location.search.includes(`id=${productId}`);
    };

  return (
    <div className={`flex h-screen w-full ${darkMode ? 'dark' : ''}`}>
      <style jsx global>{`
        :root {
          --background: 0 0% 100%;
          --foreground: 222.2 84% 4.9%;
          --card: 0 0% 100%;
          --card-foreground: 222.2 84% 4.9%;
          --popover: 0 0% 100%;
          --popover-foreground: 222.2 84% 4.9%;
          --primary: 221 83% 53%;
          --primary-foreground: 210 40% 98%;
          --secondary: 210 40% 96.1%;
          --secondary-foreground: 222.2 47.4% 11.2%;
          --muted: 210 40% 96.1%;
          --muted-foreground: 215.4 16.3% 46.9%;
          --accent: 210 40% 96.1%;
          --accent-foreground: 222.2 47.4% 11.2%;
          --destructive: 0 84.2% 60.2%;
          --destructive-foreground: 210 40% 98%;
          --border: 214.3 31.8% 91.4%;
          --input: 214.3 31.8% 91.4%;
          --ring: 221.2 83.2% 53.3%;
          --radius: 0.5rem;
        }
        
        .dark {
          --background: 222.2 84% 4.9%;
          --foreground: 210 40% 98%;
          --card: 222.2 84% 4.9%;
          --card-foreground: 210 40% 98%;
          --popover: 222.2 84% 4.9%;
          --popover-foreground: 210 40% 98%;
          --primary: 217.2 91.2% 59.8%;
          --primary-foreground: 222.2 47.4% 11.2%;
          --secondary: 217.2 32.6% 17.5%;
          --secondary-foreground: 210 40% 98%;
          --muted: 217.2 32.6% 17.5%;
          --muted-foreground: 215 20.2% 65.1%;
          --accent: 217.2 32.6% 17.5%;
          --accent-foreground: 210 40% 98%;
          --destructive: 0 62.8% 30.6%;
          --destructive-foreground: 210 40% 98%;
          --border: 217.2 32.6% 17.5%;
          --input: 217.2 32.6% 17.5%;
          --ring: 224.3 76.3% 48%;
        }
        
        body {
          background-color: hsl(var(--background));
          color: hsl(var(--foreground));
        }
        
        .dark .bg-white {
          background-color: hsl(var(--background));
        }
        
        .dark .border-gray-200 {
          border-color: hsl(var(--border));
        }
        
        .dark .text-gray-700,
        .dark .text-gray-800,
        .dark .text-gray-900 {
          color: hsl(var(--foreground));
        }
        
        .dark .text-gray-500,
        .dark .text-gray-600 {
          color: hsl(var(--muted-foreground));
        }
        
        .dark .hover\\:bg-gray-50:hover,
        .dark .hover\\:bg-gray-100:hover {
          background-color: hsl(var(--secondary));
        }
        
        .dark .bg-gray-50,
        .dark .bg-gray-100 {
          background-color: hsl(var(--muted));
        }
      `}</style>

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={"fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 shadow-lg transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0 dark:bg-gray-900 dark:border-gray-700 flex flex-col"}>
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h1 className="text-xl font-bold flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <Coins className="w-6 h-6" />
            Staking Admin
          </h1>
          <button
            className="p-2 rounded-full hover:bg-gray-100 md:hidden dark:hover:bg-gray-800"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <Link
            to={"/stakingrisk/dashboard"}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
              currentPageName === "Dashboard" 
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" 
                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
             <Link
            to={"/stakingrisk/trashbox"}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
              currentPageName === "TrashBox" 
                ? "bg-red-50 text-red-700 dark:bg-red-900/50 dark:text-red-300" 
                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <Trash2 className="w-5 h-5" />
            Trash Box
          </Link>
          <Link
           to={"/stakingRisk/create-product"}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
              currentPageName === "CreateProduct" 
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" 
                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <PlusCircle className="w-5 h-5" />
            Create Product
          </Link>
           <Link
           to={"/stakingRisk/create-category"}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
              currentPageName === "CreateProduct" 
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" 
                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            }`}            
          >
            <PlusCircle className="w-5 h-5" />
            Create Category
          </Link>
          {/* Staking Products List */}
          <div className="pt-4 mt-4 border-t dark:border-gray-700">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Products
            </h3>
            <div className="mt-2 space-y-1">                        
            {products?.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No products yet.
              </div>
            ) : (
              products?.map(product => (
                <Link
                  key={product.id}
                  to={`/stakingRisk/product-detail/${product.id}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium truncate ${
                    isActiveProductPage(product.id)
                      ? "bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  title={product.name}
                >
                  <Activity className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{product.name}</span>
                </Link>
              ))
            )}
          </div>
         </div>
        </nav>
      </aside>
      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm px-4 h-16 flex items-center justify-between dark:bg-gray-900 dark:border-gray-800 z-30">
          {/* Mobile menu button */}
          <button
            className="p-2 rounded-full hover:bg-gray-100 md:hidden dark:hover:bg-gray-800"
            onClick={() => setSidebarOpen(true)} >
            <Menu className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>          
          {/* Placeholder for breadcrumbs or page title if needed */}
          <div className="flex-1 md:block hidden"> 
          {/* <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{currentPageName}</h2> */}
          </div>
          <div className="flex items-center space-x-3 md:space-x-4">
            <button
              onClick={toggleDarkMode}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-500 dark:hover:bg-gray-800 dark:text-gray-400"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </header>
        {/* Main content area */}
        <main className="w-full flex-1 overflow-auto bg-gray-50 dark:bg-gray-900/50">
          {children}
        </main>
      </div>
    </div>
  );
}